import { Button, Form, Input, Row } from 'antd';
import getInputRules, { inputType, validateFullName } from '../../utils/getInputRules';
import PhoneInput from 'react-phone-input-2';
import classesAuth from '../../common/styles/FormStyles.module.scss';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import compareObjectValues from '../../utils/compareObjectValues';
import { useUserService } from '../../services/userService';
import classes from '../../pages/Profile/style.module.scss';
import { colors, fontSize, iconColor, primitiveColors, radius, spacing } from '../../theming';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPhoneNumberPlaceholder } from '../../utils/phoneInputHelpers';
import { useTranslation } from 'react-i18next';
import { useImpersonation } from '../../hooks/useImpersonation';

const ProfileForm = ({ form, currentUser, fetchUser, setLoading, loading, t }) => {
    const { updateProfile } = useUserService();
    const [emailChanged, setEmailChanged] = useState(false);
    const { i18n } = useTranslation();
    // not an antd control, so the page's componentDisabled does not reach it
    const { isImpersonating } = useImpersonation();

    useEffect(() => {
        form.setFieldsValue({
            phoneNumber: currentUser.phoneNumber,
            fullName: `${currentUser.firstName} ${currentUser.lastName}`,
            email: currentUser.email,
        });
    }, [currentUser, form]);

    const handleFieldsChange = changedFields => {
        const emailField = changedFields.find(field => field.name[0] === 'email');
        if (emailField) {
            setEmailChanged(emailField.value !== currentUser.email && emailField.value?.trim() !== '');
        }
    };

    const handleSubmit = async values => {
        const fullName = values.fullName || '';
        const [firstName, lastName] = fullName.split(' ');

        const valuesToSend = {
            firstName: firstName || currentUser.firstName,
            lastName: lastName || currentUser.lastName,
            ...values,
        };

        delete valuesToSend.photo;
        delete valuesToSend.fullName;

        if (compareObjectValues(valuesToSend, currentUser)) {
            return;
        }

        setLoading(prev => ({ ...prev, profile: true }));
        try {
            await updateProfile(valuesToSend);
            globalNotification.open({
                type: notificationType.SUCCESS,
                message: emailChanged
                    ? t('profile:notification.email-update.message')
                    : t('profile:notification.save-success.message'),
                description: emailChanged
                    ? t('profile:notification.email-update.description')
                    : t('profile:notification.save-success.description'),
            });

            fetchUser();
            setEmailChanged(false);
            if (values.password) {
                form.resetFields(['password']);
            }
        } catch (err) {
            console.error('error message', err.response.data);
        } finally {
            setLoading(prev => ({ ...prev, profile: false }));
        }
    };

    const inputIconStyle = {
        color: iconColor.signin,
        fontSize: fontSize.md,
        marginRight: spacing[2],
    };

    return (
        <Form
            requiredMark={false}
            form={form}
            onFinish={handleSubmit}
            style={{
                width: 'clamp(300px, 85vw, 464px)',
            }}
            onFieldsChange={handleFieldsChange}
        >
            <Form.Item
                className={classesAuth['form-item']}
                label={<span className={classesAuth['form-label']}>{t('profile:fullname')}</span>}
                name="fullName"
                rules={[
                    {
                        validator: validateFullName,
                    },
                ]}
                labelCol={{ span: 24 }}
                style={{ marginBottom: '18px' }}
            >
                <Input
                    className={classesAuth['input-login']}
                    prefix={
                        <i
                            className="fi fi-rr-user"
                            style={{
                                ...inputIconStyle,
                            }}
                        />
                    }
                />
            </Form.Item>
            <Form.Item
                className={classesAuth['form-item']}
                label={<span className={classesAuth['form-label']}>{t('profile:email')}</span>}
                name="email"
                rules={getInputRules(t, inputType.EMAIL, t('form:errors.required'), t('form:errors.email'))}
                labelCol={{ span: 24 }}
                style={{ marginBottom: '18px' }}
            >
                <Input
                    className={classesAuth['input-login']}
                    prefix={
                        <i
                            className="fi-rr-envelope"
                            style={{
                                ...inputIconStyle,
                            }}
                        />
                    }
                />
            </Form.Item>
            <AnimatePresence>
                {emailChanged && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                        <Form.Item
                            className={classesAuth['form-item']}
                            label={<span className={classesAuth['form-label']}>{t('profile:currentPassword')}</span>}
                            name="password"
                            rules={getInputRules(t, inputType.PASSWORD, t('form:errors.required'))}
                            labelCol={{ span: 24 }}
                            style={{ marginBottom: '18px' }}
                            extra={t('profile:password-explanation')}
                        >
                            <Input.Password
                                className={classesAuth['input-login']}
                                placeholder={t('profile:password-placeholder')}
                                prefix={
                                    <i
                                        className="fi-rr-lock"
                                        style={{
                                            ...inputIconStyle,
                                        }}
                                    />
                                }
                                autoComplete="false"
                            />
                        </Form.Item>
                    </motion.div>
                )}
            </AnimatePresence>
            <Form.Item
                className={classesAuth['form-item']}
                label={<span className={classesAuth['form-label']}>{t('profile:phone')}</span>}
                name="phoneNumber"
                rules={getInputRules(t, inputType.PHONE_NUMBER, null, null, false)}
                labelCol={{ span: 24 }}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        color: primitiveColors.text,
                    }}
                >
                    <i
                        className="fi fi-rs-phone-rotary"
                        style={{
                            position: 'absolute',
                            left: '14px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            ...inputIconStyle,
                            zIndex: 2,
                        }}
                    />
                    <Form.Item name="phoneNumber">
                        <PhoneInput
                            disabled={isImpersonating}
                            disableDropdown={true}
                            placeholder={getPhoneNumberPlaceholder(i18n.language)}
                            className={classes['phone-input-container']}
                            inputStyle={{
                                height: '48px',
                                fontSize: fontSize.xs,
                                width: '100%',
                                minWidth: '0',
                                padding: `${spacing[3]} ${spacing[3]} ${spacing[3]} 46px`,
                                lineHeight: '20px',
                                borderRadius: radius.md,
                                transition: 'all 0.2s',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                borderColor: primitiveColors.gray100,
                                background: colors.white,
                                color: primitiveColors.gray700,
                            }}
                            buttonStyle={{
                                display: 'none',
                            }}
                            onChange={value => form.setFieldsValue({ phoneNumber: value })}
                        />
                    </Form.Item>
                </div>
            </Form.Item>
            <Row justify="left" align="left">
                <Button loading={loading} htmlType="submit" className={classes.submitForm} type="primary" size="large">
                    {t('profile:saveButton')}
                </Button>
            </Row>
        </Form>
    );
};

export default ProfileForm;
